import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Palette, ClipboardCheck, LayoutGrid, Hammer, Ruler, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { servicesData } from '../data/servicesData';

const iconMap = {
    'Building2': Building2,
    'Palette': Palette,
    'ClipboardCheck': ClipboardCheck,
    'LayoutGrid': LayoutGrid,
    'Hammer': Hammer,
    'Ruler': Ruler
};

const ServiceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const service = servicesData.find(s => s.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!service) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Service Not Found</h2>
                    <button
                        onClick={() => navigate('/services')}
                        className="text-[#008000] hover:underline"
                    >
                        Back to Services
                    </button>
                </div>
            </div>
        );
    }

    const Icon = iconMap[service.iconName];

    return (
        <div className="pt-20 min-h-screen bg-gray-50">
            {/* Hero Header */}
            <section className="bg-gradient-to-br from-[#ffffff] via-[#e8f5e9] to-[#c8e6c9] py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <Link
                        to="/services"
                        className="inline-flex items-center text-[#008000] mb-8 hover:underline font-medium"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Services
                    </Link>
                    <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                        <div className="p-4 bg-white rounded-2xl shadow-lg">
                            {Icon && <Icon className="w-16 h-16 text-[#008000]" />}
                        </div>
                        <div>
                            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                {service.title}
                            </h1>
                            <p className="text-xl text-gray-700 max-w-2xl">
                                {service.description}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Detail Content */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid lg:grid-cols-3 gap-12">

                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-8">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
                            >
                                <h2 className="text-2xl font-bold mb-4 text-gray-900">About {service.title}</h2>
                                <div className="prose prose-lg text-gray-600">
                                    <p className="whitespace-pre-line leading-relaxed">
                                        {service.longDescription}
                                    </p>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
                            >
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Key Features</h2>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {service.features.map((feature, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <CheckCircle2 className="w-5 h-5 text-[#008000] mt-1 shrink-0" />
                                            <span className="text-gray-700">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>

                        {/* Sidebar CTA */}
                        <div className="lg:col-span-1">
                            <div className="bg-[#008000] rounded-2xl p-8 text-white sticky top-24">
                                <h3 className="text-2xl font-bold mb-4">Ready to Start?</h3>
                                <p className="mb-6 text-white/90">
                                    Get in touch with us to discuss your {service.title.toLowerCase()} needs. We are here to help you build your dream.
                                </p>
                                <Link
                                    to="/contact"
                                    className="block w-full bg-white text-[#008000] text-center font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Contact Us
                                </Link>
                                <div className="mt-8 pt-8 border-t border-white/20">
                                    <p className="font-semibold mb-2">Have questions?</p>
                                    <p className="text-white/90">Call us at <br /> <a href="tel:+919039200574" className="hover:underline">+91-9039200574</a></p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServiceDetail;
